import {
  streamChatMessage,
  type StreamEvent,
  StreamEventType,
  type StreamMessage,
  StreamMessageType,
  type StreamRequest,
} from '@/api/queries/prince';
import {
  type AiMessage,
  type AutoContinuationState,
  type Message,
  MessageType,
} from '@/features/prince/messages/types';
import { Agent, Model } from '@/features/prince/models';
import { MessagePartType, type ToolCallSummary } from '@/features/types/tools';
import { throttle } from '@/utils/helpers';

/**
 * Wrapper function that aggreates the streaming events into UI relevant events.
 * This function:
 * - Aggregates tokens into text content
 * - Handles tool calls and their results
 * - Provides callbacks for UI updates
 */
export async function streamMessage(
  request: StreamRequest,
  agent: Agent,
  model: Model,
  conversationId: string,
  events: {
    onAIResponseStart: (message: Message) => void;
    onAIResponseUpdates: (message: Message) => void;
    onAIResponseComplete: (message: Message) => void;
    onError: (error: string) => void;
  },
  abortController?: AbortController
) {
  const { onAIResponseStart, onAIResponseUpdates, onError, onAIResponseComplete } = events;

  const throttledAIResponseUpdates = throttle(onAIResponseUpdates);

  const aiMessage: AiMessage = {
    id: request.run_id,
    agentRunId: request.run_id,
    type: MessageType.AI_AGENT,
    content: [],
    timestamp: new Date().toISOString(),
    attachments: [],
    isStreaming: true,
  };

  const contentParts: Array<string | ToolCallSummary> = [];

  onAIResponseStart({ ...aiMessage });

  try {
    for await (const event of streamChatMessage(
      request,
      model,
      conversationId,
      agent,
      abortController
    )) {
      switch (event.type) {
        case StreamEventType.Token:
          handleTokenEvent(event, contentParts, aiMessage, throttledAIResponseUpdates);
          break;

        case StreamEventType.Message:
          handleMessageEvent(event, contentParts, aiMessage, throttledAIResponseUpdates);
          break;

        case StreamEventType.Done:
          aiMessage.isStreaming = false;

          // Flush before sending the signal that streaming is complete
          throttledAIResponseUpdates.flush();
          onAIResponseComplete({ ...aiMessage, content: [...contentParts] });
          break;

        case StreamEventType.Error: {
          if (event.isAbortError) return;

          // Call error handler but continue to wait for DoneEvent
          onError(event.error);
          break;
        }
      }
    }
  } catch (error) {
    console.error('Unexpected error in sendMessage:', error);
    onError(error instanceof Error ? error.message : 'Unknown streaming error');
  } finally {
    throttledAIResponseUpdates.flush();
  }
}

/**
 * Handles token events by appending content to existing text or creating new text elements
 */
function handleTokenEvent(
  event: { content?: string },
  contentParts: Array<string | ToolCallSummary>,
  aiMessage: AiMessage,
  onAIResponseUpdates: (message: Message) => void
) {
  if (event.content) {
    // Append to the last text element or create a new one
    if (typeof contentParts[contentParts.length - 1] === 'string') {
      contentParts[contentParts.length - 1] += event.content;
    } else {
      contentParts.push(event.content);
    }

    onAIResponseUpdates({
      ...aiMessage,
      content: [...contentParts],
    });
  }
}

/**
 * Handles message events including AI messages with tool calls and tool call results
 */
function handleMessageEvent(
  event: Extract<StreamEvent, { type: StreamEventType.Message }>,
  contentParts: Array<string | ToolCallSummary>,
  aiMessage: AiMessage,
  onAIResponseUpdates: (message: Message) => void
) {
  if (event.content) {
    updateAutoContinuationState(event.content, contentParts, aiMessage, onAIResponseUpdates);
    if (
      event.content.type === StreamMessageType.Ai &&
      event.content.tool_calls &&
      event.content.tool_calls.length > 0
    ) {
      handleAIMessageWithToolCalls(event.content, contentParts, aiMessage, onAIResponseUpdates);
    } else if (event.content.type === 'tool') {
      handleToolCallResult(event.content, contentParts, aiMessage, onAIResponseUpdates);
    }
  }
}

function updateAutoContinuationState(
  streamMessage: StreamMessage,
  contentParts: Array<string | ToolCallSummary>,
  aiMessage: AiMessage,
  onAIResponseUpdates: (message: Message) => void
) {
  if (streamMessage.type !== StreamMessageType.Ai) return;

  const nextState = extractAutoContinuationState(streamMessage);
  if (!nextState) return;

  if (hasAutoContinuationChanged(aiMessage.autoContinuation, nextState)) {
    aiMessage.autoContinuation = nextState;
    onAIResponseUpdates({
      ...aiMessage,
      content: [...contentParts],
    });
  }
}

function extractAutoContinuationState(message: StreamMessage): AutoContinuationState | undefined {
  if (message.type !== StreamMessageType.Ai) return undefined;

  const metadata = message.response_metadata;
  const auto = metadata?.auto_continuation;

  if (!auto) {
    return undefined;
  }

  const limitReached = Boolean(auto?.limit_reached);

  if (!limitReached) {
    return undefined;
  }

  const continuationAttempts =
    typeof auto.continuation_attempts === 'number'
      ? auto.continuation_attempts
      : typeof auto.continuation_count === 'number'
        ? auto.continuation_count
        : undefined;

  const maxAutoContinuations =
    typeof auto.max_auto_continuations === 'number' ? auto.max_auto_continuations : undefined;

  return {
    limitReached,
    continuationAttempts,
    maxAutoContinuations,
  };
}

function hasAutoContinuationChanged(
  current: AutoContinuationState | undefined,
  next: AutoContinuationState
): boolean {
  if (!current) return true;

  return (
    current.limitReached !== next.limitReached ||
    current.continuationAttempts !== next.continuationAttempts ||
    current.maxAutoContinuations !== next.maxAutoContinuations
  );
}

/**
 * Handles AI messages that contain tool calls by adding them to the content array
 */
function handleAIMessageWithToolCalls(
  content: StreamMessage,
  contentParts: Array<string | ToolCallSummary>,
  aiMessage: AiMessage,
  onAIResponseUpdates: (message: Message) => void
) {
  // Handle AI message with tool calls - add tool calls to the content array
  for (const toolCall of content.tool_calls) {
    const newToolCall: ToolCallSummary = {
      id: toolCall.id,
      name: toolCall.name,
      args: toolCall.args,
      type: MessagePartType.ToolCall,
      isRunning: true,
    };
    contentParts.push(newToolCall);
  }

  onAIResponseUpdates({
    ...aiMessage,
    content: [...contentParts],
  });
}

/**
 * Handles tool call results by updating the corresponding pending tool call
 */
function handleToolCallResult(
  content: StreamMessage,
  contentParts: Array<string | ToolCallSummary>,
  aiMessage: AiMessage,
  onAIResponseUpdates: (message: Message) => void
) {
  // Handle tool call result - update the proper pending tool call with its results
  const toolCallIndex = contentParts.findIndex(
    part => typeof part !== 'string' && part.id === content.tool_call_id
  );

  if (toolCallIndex !== -1) {
    const existingToolCall = contentParts[toolCallIndex] as ToolCallSummary;
    const updatedToolCall: ToolCallSummary = {
      ...existingToolCall,
      results: content.content,
      isRunning: false,
    };
    contentParts[toolCallIndex] = updatedToolCall;

    onAIResponseUpdates({
      ...aiMessage,
      content: [...contentParts],
    });
  }
}
